import { BottomLink } from '@/components/auth/bottom-link';
import { Logo } from '@/components/shared/logo';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Routes } from '@/lib/routes';
import { getLocale, type Locale } from '@/lib/locale';
import { m } from '@/locale/paraglide/messages';
import { Link } from '@tanstack/react-router';
import { cn } from '@/lib/utils';

export type AuthPresentation = 'page' | 'modal';

const MODAL_DESCRIPTION_COPY = {
  en: 'Continue creating with Grok Imagine',
  es: 'Sigue creando con Grok Imagine',
} as const satisfies Record<Locale, string>;

interface AuthCardProps {
  children: React.ReactNode;
  headerLabel: string;
  bottomButtonLabel: string;
  bottomButtonHref: string;
  bottomButtonSearch?: Record<string, string>;
  className?: string;
  description?: string | null;
  presentation?: AuthPresentation;
  showFooter?: boolean;
}

export function AuthCard({
  children,
  headerLabel,
  bottomButtonLabel,
  bottomButtonHref,
  bottomButtonSearch,
  className,
  description,
  presentation = 'page',
  showFooter = true,
}: AuthCardProps) {
  const isModal = presentation === 'modal';
  const modalDescription =
    isModal && description !== null
      ? (description ?? MODAL_DESCRIPTION_COPY[getLocale()])
      : null;

  return (
    <Card
      data-presentation={presentation}
      className={cn(
        isModal
          ? 'gap-0 rounded-xl border border-border/80 bg-popover py-0 shadow-[0_28px_90px_rgba(0,0,0,0.58)] ring-0'
          : 'border border-border pt-6 shadow-xs',
        className
      )}
      size="default"
    >
      <CardHeader
        className={cn(
          'flex flex-col items-center',
          isModal && 'gap-0 px-7 pt-7'
        )}
      >
        <Link to="/" search={{}}>
          <Logo className={cn('mb-2', isModal && 'mb-3 size-11')} />
        </Link>
        {isModal ? (
          <>
            <CardTitle className="text-center font-semibold text-xl leading-tight">
              {headerLabel}
            </CardTitle>
            {modalDescription ? (
              <CardDescription className="mt-1 text-center text-sm leading-5">
                {modalDescription}
              </CardDescription>
            ) : null}
          </>
        ) : (
          <CardDescription>{headerLabel}</CardDescription>
        )}
      </CardHeader>
      <CardContent className={cn(isModal && 'px-7 pt-5')}>
        {children}
      </CardContent>
      {isModal && (
        <div className="flex items-center justify-center gap-2 px-7 pt-4 pb-5 text-[0.7rem] text-muted-foreground">
          <Link
            to={Routes.TermsOfService}
            search={{}}
            className="transition-colors hover:text-foreground"
          >
            {m.auth_common_terms_of_service()}
          </Link>
          <span aria-hidden="true">·</span>
          <Link
            to={Routes.PrivacyPolicy}
            search={{}}
            className="transition-colors hover:text-foreground"
          >
            {m.auth_common_privacy_policy()}
          </Link>
        </div>
      )}
      {showFooter && (
        <CardFooter>
          <BottomLink
            label={bottomButtonLabel}
            href={bottomButtonHref}
            search={bottomButtonSearch}
          />
        </CardFooter>
      )}
    </Card>
  );
}
